/**
 * Turn pasted rich-text HTML into page-document block descriptors so a paste
 * lands as REAL blocks (paragraphs → separate text blocks, list items →
 * bullet/numbered items, headings → heading blocks, blockquotes → quotes,
 * pre → code) instead of being coerced into one inline paragraph.
 *
 * The parser is host-agnostic: it emits neutral `PastedBlock` descriptors and
 * the host maps them to its element-type ids. One descriptor per block — list
 * items each become their own descriptor (page lists are one block per item).
 */

import type { DocumentElement } from "@eaco/forms-core";

/**
 * Private clipboard MIME for lossless intra-app block copy/paste. The payload
 * carries the full `DocumentElement[]` (types, data incl. indent, nested
 * children) so a paste inside the page editor reconstructs the blocks
 * verbatim (with fresh ids — the HOST regenerates ids on insert). `text/html`
 * rides alongside on the clipboard for pasting into external apps.
 */
export const EACO_PAGE_BLOCKS_MIME = "application/x-eaco-page-blocks";

interface EacoPageBlocksPayload {
  version: 1;
  /**
   * Payload flavour. Absent / `"blocks"` = whole blocks (batch-2 multi-block
   * copy). `"text-range"` = a CHARACTER-GRANULAR cross-block selection: the
   * partially-selected head/tail block content rides as HTML alongside the
   * fully-covered middle blocks.
   */
  kind?: "blocks" | "text-range";
  blocks: DocumentElement[];
  /** text-range only: HTML of the head block's selected part (caret → end). */
  headHtml?: string;
  /** text-range only: HTML of the tail block's selected part (start → caret). */
  tailHtml?: string;
}

/** Serialise blocks for the private clipboard MIME. */
export function encodePageBlocks(blocks: DocumentElement[]): string {
  const payload: EacoPageBlocksPayload = { version: 1, blocks };
  return JSON.stringify(payload);
}

/**
 * Serialise a cross-block TEXT-RANGE copy (head partial + covered blocks +
 * tail partial) for the private clipboard MIME.
 */
export function encodePageTextRange(payload: {
  headHtml: string;
  tailHtml: string;
  blocks: DocumentElement[];
}): string {
  const full: EacoPageBlocksPayload = {
    version: 1,
    kind: "text-range",
    blocks: payload.blocks,
    headHtml: payload.headHtml,
    tailHtml: payload.tailHtml,
  };
  return JSON.stringify(full);
}

const htmlHasText = (html: unknown): html is string =>
  typeof html === "string" && html.replace(/<[^>]*>/g, "").trim().length > 0;

/** A synthetic plain-text block carrying a text-range head/tail partial. The
 *  host regenerates ids on insert, so the placeholder id never lands. */
const synthTextBlock = (html: string, id: string): DocumentElement => ({
  id,
  typeId: "page-text",
  name: "Text",
  data: { content: html },
});

/** Parse the private clipboard MIME; null when absent/malformed. */
export function decodePageBlocks(raw: string): DocumentElement[] | null {
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw) as Partial<EacoPageBlocksPayload>;
    if (payload?.version !== 1 || !Array.isArray(payload.blocks)) return null;
    const blocks = payload.blocks.filter(
      (b): b is DocumentElement =>
        !!b && typeof b === "object" && typeof b.typeId === "string",
    );
    if (payload.kind === "text-range") {
      // A text-range paste lands as blocks: head partial → text block, the
      // covered middle verbatim, tail partial → text block. Content-lossless
      // for intra-app paste without every paste path learning the new shape.
      const parts: DocumentElement[] = [];
      if (htmlHasText(payload.headHtml)) {
        parts.push(synthTextBlock(payload.headHtml, "clip-range-head"));
      }
      parts.push(...blocks);
      if (htmlHasText(payload.tailHtml)) {
        parts.push(synthTextBlock(payload.tailHtml, "clip-range-tail"));
      }
      return parts.length > 0 ? parts : null;
    }
    return blocks.length > 0 ? blocks : null;
  } catch {
    return null;
  }
}

export interface PastedBlock {
  kind: "text" | "heading" | "bullet" | "numbered" | "quote" | "code";
  /** Headings only: 1-4 (deeper HTML headings clamp to 4). */
  level?: number;
  /**
   * Inline HTML for rich kinds (text/bullet/numbered/quote), plain text for
   * `code`, plain text for `heading` (headings are plain surfaces).
   */
  content: string;
}

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "UL",
  "OL",
  "LI",
  "BLOCKQUOTE",
  "PRE",
  "TABLE",
  "SECTION",
  "ARTICLE",
  "FIGURE",
  "HEADER",
  "FOOTER",
]);

/** Strip block-level structure from a fragment, keeping inline markup. */
function inlineHtml(el: Element): string {
  const clone = el.cloneNode(true) as Element;
  // Drop nested lists — they're emitted as their own descriptors.
  clone.querySelectorAll("ul, ol").forEach((n) => n.remove());
  // Unwrap any nested block containers so only inline markup remains.
  let changed = true;
  while (changed) {
    changed = false;
    for (const n of Array.from(clone.querySelectorAll("*"))) {
      if (BLOCK_TAGS.has(n.tagName)) {
        n.replaceWith(...Array.from(n.childNodes));
        changed = true;
        break;
      }
    }
  }
  return clone.innerHTML.trim();
}

function hasText(el: Element): boolean {
  return (el.textContent ?? "").trim().length > 0;
}

function walkList(
  list: Element,
  kind: "bullet" | "numbered",
  out: PastedBlock[],
) {
  for (const li of Array.from(list.children)) {
    if (li.tagName !== "LI") continue;
    const content = inlineHtml(li);
    if (content) out.push({ kind, content });
    // Nested lists become further (flattened) items of their own kind.
    for (const nested of Array.from(li.querySelectorAll(":scope > ul, :scope > ol"))) {
      walkList(nested, nested.tagName === "OL" ? "numbered" : "bullet", out);
    }
  }
}

function walkNode(node: Element, out: PastedBlock[]) {
  const tag = node.tagName;
  if (/^H[1-6]$/.test(tag)) {
    const text = (node.textContent ?? "").trim();
    if (text)
      out.push({
        kind: "heading",
        level: Math.min(Number(tag[1]), 4),
        content: text,
      });
    return;
  }
  if (tag === "UL" || tag === "OL") {
    walkList(node, tag === "OL" ? "numbered" : "bullet", out);
    return;
  }
  if (tag === "PRE") {
    const text = (node.textContent ?? "").replace(/\n+$/, "");
    if (text.trim()) out.push({ kind: "code", content: text });
    return;
  }
  if (tag === "BLOCKQUOTE") {
    // A quote keeps its inner paragraphs as line breaks in one quote block.
    const parts = Array.from(node.children).filter(
      (c) => BLOCK_TAGS.has(c.tagName) && hasText(c),
    );
    const content = parts.length
      ? parts.map((p) => inlineHtml(p)).filter(Boolean).join("<br>")
      : inlineHtml(node);
    if (content) out.push({ kind: "quote", content });
    return;
  }
  if (tag === "P") {
    const content = inlineHtml(node);
    if (content) out.push({ kind: "text", content });
    return;
  }
  // Containers (div/section/table/…): if they hold block children, recurse;
  // otherwise treat their inline content as one text block.
  const blockChildren = Array.from(node.children).filter((c) =>
    BLOCK_TAGS.has(c.tagName),
  );
  if (blockChildren.length > 0) {
    for (const child of Array.from(node.children)) {
      if (BLOCK_TAGS.has(child.tagName)) {
        walkNode(child, out);
      } else if (hasText(child)) {
        const content = inlineHtml(child);
        if (content) out.push({ kind: "text", content });
      }
    }
    return;
  }
  if (hasText(node)) {
    const content = inlineHtml(node);
    if (content) out.push({ kind: "text", content });
  }
}

/**
 * Parse clipboard HTML into block descriptors. Returns null when the paste is
 * a single plain text fragment — the caller should fall through to the
 * default inline paste (which preserves the caret position naturally).
 */
export function parsePastedHtml(html: string): PastedBlock[] | null {
  if (!html) return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return null;
  }
  const body = doc.body;
  if (!body || !hasText(body)) return null;

  const out: PastedBlock[] = [];
  // Top-level walk: block elements map to descriptors; runs of loose inline
  // nodes between them collapse into text blocks.
  let inlineRun: string[] = [];
  const flushInline = () => {
    const joined = inlineRun.join("").trim();
    if (joined) {
      out.push({ kind: "text", content: joined });
    }
    inlineRun = [];
  };
  for (const node of Array.from(body.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (BLOCK_TAGS.has(el.tagName)) {
        flushInline();
        walkNode(el, out);
        continue;
      }
      inlineRun.push(el.outerHTML);
      continue;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      inlineRun.push(node.textContent ?? "");
    }
  }
  flushInline();

  if (out.length === 0) return null;
  // A single text fragment isn't worth block-splitting — let the editor's
  // native paste keep the caret flow. (Single non-text blocks — one list
  // item, a heading, a quote — DO convert.)
  if (out.length === 1 && out[0].kind === "text") return null;
  return out;
}
